import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import TaskIntegrityService from '../../services/integrity/TaskIntegrityService';
import CollegeEventBus from '../../services/events/CollegeEventBus';
import { toast } from 'react-hot-toast';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Activity,
  Users,
  TrendingUp,
  RefreshCw,
  Play,
  Search,
  Filter,
  Download,
  Eye,
  Settings,
  Zap,
  Clock,
  Database,
  FileText
} from 'lucide-react';

/**
 * TaskIntegrityDashboard - Admin oversight and verification tools
 *
 * Features:
 * - Health overview (students with complete tasks, coverage, issues)
 * - Student-level verification with one-click auto-heal
 * - College-level deep dive (expected vs actual tasks)
 * - Batch operations (regenerate by filter)
 * - Audit log viewer
 * - Event history
 */
export default function TaskIntegrityDashboard() {
  const [loading, setLoading] = useState(true);
  const [healthStats, setHealthStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [healing, setHealing] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [eventHistory, setEventHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'students' | 'audit' | 'events'
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    graduationYear: '',
    hasIssues: false
  });

  useEffect(() => {
    loadDashboard();
  }, []);

  /**
   * Load dashboard data
   */
  const loadDashboard = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadHealthStats(),
        loadStudents(),
        loadRecentAuditLogs(),
        loadEventHistory()
      ]);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load health statistics
   */
  const loadHealthStats = async () => {
    try {
      // Get all students
      const { data: allStudents } = await supabase
        .from('student_academic_profiles')
        .select('user_id, graduation_year');

      // Get students with timeline items
      const { data: studentsWithTasks } = await supabase
        .from('timeline_items')
        .select('user_id')
        .in('user_id', allStudents?.map(s => s.user_id) || []);

      const uniqueWithTasks = new Set(studentsWithTasks?.map(t => t.user_id) || []);

      // Count tasks by round
      const { data: tasksByRound } = await supabase
        .from('timeline_items')
        .select('application_round')
        .not('application_round', 'is', null);

      const roundCoverage = {
        ed: tasksByRound?.filter(t => t.application_round === 'early_decision').length || 0,
        ea: tasksByRound?.filter(t => t.application_round === 'early_action').length || 0,
        rd: tasksByRound?.filter(t => t.application_round === 'regular').length || 0
      };

      // Get recent issues from audit log
      const { data: recentIssues } = await supabase
        .from('task_audit_log')
        .select('*')
        .in('operation_type', ['integrity_check', 'integrity_heal', 'round_changed'])
        .order('created_at', { ascending: false })
        .limit(100);

      const issuesByType = {
        high: recentIssues?.filter(i => i.metadata?.severity === 'high').length || 0,
        medium: recentIssues?.filter(i => i.metadata?.severity === 'medium').length || 0,
        low: recentIssues?.filter(i => i.metadata?.severity === 'low').length || 0
      };

      setHealthStats({
        totalStudents: allStudents?.length || 0,
        studentsWithTasks: uniqueWithTasks.size,
        completionRate: Math.round((uniqueWithTasks.size / (allStudents?.length || 1)) * 100),
        roundCoverage,
        issues: issuesByType,
        lastChecked: new Date().toISOString()
      });

    } catch (error) {
      console.error('Failed to load health stats:', error);
    }
  };

  /**
   * Load students list
   */
  const loadStudents = async () => {
    try {
      const { data: profiles } = await supabase
        .from('student_academic_profiles')
        .select(`
          user_id,
          graduation_year,
          gpa_weighted
        `);

      const { data: users } = await supabase
        .from('family_members')
        .select('id, name, email')
        .in('id', profiles?.map(p => p.user_id) || []);

      const studentsData = await Promise.all(
        (profiles || []).map(async (profile) => {
          const user = users?.find(u => u.id === profile.user_id);

          // Get college count
          const { data: colleges } = await supabase
            .from('college_journey_colleges')
            .select('id')
            .eq('user_id', profile.user_id);

          // Get task count
          const { data: tasks } = await supabase
            .from('timeline_items')
            .select('id')
            .eq('user_id', profile.user_id);

          return {
            userId: profile.user_id,
            name: user?.name || 'Unknown',
            email: user?.email || '',
            graduationYear: profile.graduation_year,
            gpa: profile.gpa_weighted,
            collegeCount: colleges?.length || 0,
            taskCount: tasks?.length || 0
          };
        })
      );

      setStudents(studentsData);

    } catch (error) {
      console.error('Failed to load students:', error);
    }
  };

  /**
   * Load recent audit logs
   */
  const loadRecentAuditLogs = async () => {
    try {
      const { data: logs } = await supabase
        .from('task_audit_log')
        .select(`
          *,
          family_members!user_id (name),
          college_journey_colleges!college_id (name)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      setAuditLogs(logs || []);

    } catch (error) {
      console.error('Failed to load audit logs:', error);
    }
  };

  /**
   * Load event history
   */
  const loadEventHistory = () => {
    const history = CollegeEventBus.getEventHistory({ limit: 50 });
    setEventHistory(history);
  };

  /**
   * Verify student tasks
   */
  const verifyStudent = async (userId) => {
    setVerifying(true);
    setSelectedStudent(userId);
    setVerificationResult(null);

    try {
      // Get all colleges for student
      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select('id, name')
        .eq('user_id', userId);

      if (!colleges || colleges.length === 0) {
        setVerificationResult({
          userId,
          colleges: [],
          overallHealth: true,
          message: 'No colleges found for this student'
        });
        return;
      }

      // Verify each college
      const collegeVerifications = await Promise.all(
        colleges.map(async (college) => {
          const verification = await TaskIntegrityService.verifyCollegeTasks(
            college.id,
            userId,
            { verbose: false }
          );
          return {
            ...verification,
            collegeName: college.name
          };
        })
      );

      const hasIssues = collegeVerifications.some(v => !v.isHealthy);

      setVerificationResult({
        userId,
        colleges: collegeVerifications,
        overallHealth: !hasIssues,
        totalMissing: collegeVerifications.reduce((sum, v) => sum + v.missing.length, 0),
        totalOrphaned: collegeVerifications.reduce((sum, v) => sum + v.orphaned.length, 0),
        totalMismatched: collegeVerifications.reduce((sum, v) => sum + v.mismatched.length, 0)
      });

      toast.success(`Verification complete for ${colleges.length} colleges`);

    } catch (error) {
      console.error('Verification failed:', error);
      toast.error('Verification failed: ' + error.message);
    } finally {
      setVerifying(false);
    }
  };

  /**
   * Auto-heal student issues
   */
  const autoHealStudent = async (userId) => {
    if (!verificationResult) return;

    setHealing(true);
    try {
      const healResults = [];

      for (const collegeVerif of verificationResult.colleges) {
        if (!collegeVerif.isHealthy) {
          const result = await TaskIntegrityService.autoHealCollege(
            collegeVerif.college.id,
            userId,
            collegeVerif
          );
          healResults.push({
            collegeName: collegeVerif.collegeName,
            ...result
          });
        }
      }

      const successCount = healResults.filter(r => r.success).length;
      toast.success(`Auto-healed ${successCount}/${healResults.length} colleges`);

      // Refresh verification
      await verifyStudent(userId);

    } catch (error) {
      console.error('Auto-heal failed:', error);
      toast.error('Auto-heal failed: ' + error.message);
    } finally {
      setHealing(false);
    }
  };

  /**
   * Run integrity audit
   */
  const runIntegrityAudit = async () => {
    toast.loading('Running integrity audit...', { id: 'audit' });

    try {
      const result = await TaskIntegrityService.runIntegrityAudit({
        autoHeal: true,
        confidenceThreshold: 0.8,
        limit: 50
      });

      toast.success(
        `Audit complete: ${result.issuesFound} issues found, ${result.autoHealed} auto-healed`,
        { id: 'audit' }
      );

      // Reload dashboard
      await loadDashboard();

    } catch (error) {
      console.error('Integrity audit failed:', error);
      toast.error('Audit failed: ' + error.message, { id: 'audit' });
    }
  };

  // Filter students
  const filteredStudents = students.filter(student => {
    if (searchQuery && !student.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filters.graduationYear && student.graduationYear !== parseInt(filters.graduationYear)) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-azure-500" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="h-8 w-8 text-azure-500" />
              Task Integrity Dashboard
            </h1>
            <p className="text-slate-600 mt-1">Monitor and maintain timeline task health</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadDashboard}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg hover:bg-stone-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button
              onClick={runIntegrityAudit}
              className="flex items-center gap-2 px-4 py-2 bg-azure-500 text-white rounded-lg hover:bg-azure-600"
            >
              <Play className="h-4 w-4" />
              Run Audit
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-stone-200">
        {[
          { id: 'overview', label: 'Overview', icon: Activity },
          { id: 'students', label: 'Students', icon: Users },
          { id: 'audit', label: 'Audit Log', icon: FileText },
          { id: 'events', label: 'Events', icon: Zap }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-azure-500 text-azure-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && healthStats && (
        <div className="space-y-6">
          {/* Health Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-600">Students with Tasks</h3>
                <Users className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900">
                {healthStats.studentsWithTasks}/{healthStats.totalStudents}
              </div>
              <div className="mt-2 text-sm text-slate-600">
                {healthStats.completionRate}% coverage
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-600">Round Coverage</h3>
                <TrendingUp className="h-5 w-5 text-azure-500" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">ED Tasks:</span>
                  <span className="font-medium">{healthStats.roundCoverage.ed}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">EA Tasks:</span>
                  <span className="font-medium">{healthStats.roundCoverage.ea}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">RD Tasks:</span>
                  <span className="font-medium">{healthStats.roundCoverage.rd}</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-600">Detected Issues</h3>
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-red-600">High:</span>
                  <span className="font-medium">{healthStats.issues.high}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-orange-600">Medium:</span>
                  <span className="font-medium">{healthStats.issues.medium}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-yellow-600">Low:</span>
                  <span className="font-medium">{healthStats.issues.low}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-lg border border-stone-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={runIntegrityAudit}
                className="flex items-center gap-3 p-4 border-2 border-stone-200 rounded-lg hover:border-azure-500 hover:bg-azure-50 transition-all"
              >
                <div className="p-2 bg-azure-100 rounded-lg">
                  <Play className="h-5 w-5 text-azure-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-slate-900">Run Full Audit</div>
                  <div className="text-sm text-slate-600">Check all students for issues</div>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('students')}
                className="flex items-center gap-3 p-4 border-2 border-stone-200 rounded-lg hover:border-azure-500 hover:bg-azure-50 transition-all"
              >
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Users className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-slate-900">Verify Students</div>
                  <div className="text-sm text-slate-600">Check individual student tasks</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'students' && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg border border-stone-200 flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
              />
            </div>
            <select
              value={filters.graduationYear}
              onChange={(e) => setFilters({ ...filters, graduationYear: e.target.value })}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
            >
              <option value="">All Years</option>
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>

          {/* Students List */}
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Year</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Colleges</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Tasks</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {filteredStudents.map(student => (
                  <tr key={student.userId} className="hover:bg-stone-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-slate-900">{student.name}</div>
                        <div className="text-sm text-slate-600">{student.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{student.graduationYear}</td>
                    <td className="px-6 py-4 text-slate-600">{student.collegeCount}</td>
                    <td className="px-6 py-4 text-slate-600">{student.taskCount}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => verifyStudent(student.userId)}
                        disabled={verifying}
                        className="inline-flex items-center gap-2 px-3 py-1 text-sm bg-azure-500 text-white rounded-lg hover:bg-azure-600 disabled:opacity-50"
                      >
                        <Eye className="h-4 w-4" />
                        Verify
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Verification Result */}
          {verificationResult && (
            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Verification Results</h3>
                <div className="flex items-center gap-2">
                  {verificationResult.overallHealth ? (
                    <span className="flex items-center gap-2 text-emerald-600">
                      <CheckCircle className="h-5 w-5" />
                      Healthy
                    </span>
                  ) : (
                    <>
                      <span className="flex items-center gap-2 text-orange-600">
                        <AlertTriangle className="h-5 w-5" />
                        Issues Found
                      </span>
                      <button
                        onClick={() => autoHealStudent(verificationResult.userId)}
                        disabled={healing}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                      >
                        {healing ? 'Healing...' : 'Auto-Heal'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {!verificationResult.overallHealth && (
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-orange-600">{verificationResult.totalMissing}</div>
                      <div className="text-sm text-slate-600">Missing Tasks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">{verificationResult.totalOrphaned}</div>
                      <div className="text-sm text-slate-600">Orphaned Tasks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">{verificationResult.totalMismatched}</div>
                      <div className="text-sm text-slate-600">Mismatched Tasks</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {verificationResult.colleges.map((college, idx) => (
                  <div key={idx} className="p-4 border border-stone-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{college.collegeName}</h4>
                      {college.isHealthy ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600">Expected:</span>
                        <span className="ml-2 font-medium">{college.expected}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Actual:</span>
                        <span className="ml-2 font-medium">{college.actual}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Missing:</span>
                        <span className="ml-2 font-medium text-orange-600">{college.missing.length}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-200">
            <h3 className="text-lg font-semibold text-slate-900">Recent Audit Logs</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Operation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">College</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {auditLogs.slice(0, 20).map(log => (
                  <tr key={log.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      {log.operation_type}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {log.family_members?.name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {log.college_journey_colleges?.name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {JSON.stringify(log.metadata).substring(0, 50)}...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'events' && (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Event History</h3>
            <button
              onClick={loadEventHistory}
              className="text-sm text-azure-600 hover:text-azure-700"
            >
              Refresh
            </button>
          </div>
          <div className="p-4 space-y-3">
            {eventHistory.map(event => (
              <div key={event.id} className="p-4 border border-stone-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-900">{event.type}</span>
                  <span className="text-sm text-slate-600">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="text-sm text-slate-600">
                  {JSON.stringify(event.payload, null, 2)}
                </div>
                {event.handlerResults && event.handlerResults.length > 0 && (
                  <div className="mt-2 text-xs text-slate-500">
                    {event.handlerResults.filter(r => r.success).length}/{event.handlerResults.length} handlers succeeded
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
